// app/app.config.ts
import { LoggerModule, NgxLoggerLevel, TOKEN_LOGGER_WRITER_SERVICE } from 'ngx-logger';
import { environment } from '../environments/environment';
import { ApplicationConfig, provideZoneChangeDetection, isDevMode, InjectionToken, inject, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { reducers } from './state/reducers';
import { effects } from './state/effects/index';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { withCredentialsInterceptor } from './core/interceptors/with-credentials.interceptor';
import { provideMarkdown, MARKED_OPTIONS } from 'ngx-markdown';
import { provideFirebaseApp, initializeApp, FirebaseApp, getApps, getApp  } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideServiceWorker } from '@angular/service-worker';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { RequestCounterService } from './core/services/request-counter.service';
import MyPreset from './theme/mypreset';
import { ApiDefApiConfiguration } from '@api/api-def-api-configuration';
import { LoggerWriterService } from './core/logger/logger-writer.service';
import { safeStoreMetaReducers } from './core/storage/safe-store-persistence';

export const DEFAULT_APP = new InjectionToken<FirebaseApp>('default.app');

const currentEnvironments: { [key: string]: string } = {
  "development": "development",
  "development_emulator": "development",
  "staging": "staging",
  "staging_emulator": "staging",
  "production": "production",
  "production_emulator": "production",
}
export const getCurrentEnvironment = () => currentEnvironments[environment.environmentName];

const developmentOnlyProviders = environment.production
  ? []
  : [provideStoreDevtools({ maxAge: 25 })];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes), // withDebugTracing()
    importProvidersFrom(LoggerModule.forRoot({
      level: environment.production ? NgxLoggerLevel.WARN : NgxLoggerLevel.TRACE,
      serverLogLevel: NgxLoggerLevel.ERROR,
    },
    {
      writerProvider: {
        provide: TOKEN_LOGGER_WRITER_SERVICE,
        useClass: LoggerWriterService,
      }
    })),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        withCredentialsInterceptor,
        errorInterceptor,
        loadingInterceptor,
      ])
    ),
    {
      provide: ApiDefApiConfiguration,
      useValue: {
        rootUrl: environment.useEmulatorApi ? environment.apiUrlEmulator : environment.apiUrl,
      }
    },
    provideStore(reducers,
      {
        metaReducers: safeStoreMetaReducers,
      }
    ),
    provideEffects(effects),
    ...developmentOnlyProviders,
    provideAnimationsAsync(),
    providePrimeNG({
        ripple: false,
        theme: {
            preset: MyPreset,
            options: {
                darkModeSelector: '.my-app-dark', 
                cssLayer: {
                  name: 'primeng',
                  order: 'tailwind-base, primeng, tailwind-utilities'
              }
            }
        }
    }),
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: true,
          breaks: true,
          pedantic: false
        }
      }
    }),
    provideFirebaseApp(() => {
      const existingApp = getApps().find(app => app.name === '[DEFAULT]');
      return existingApp || initializeApp(environment.firebaseConfig, '[DEFAULT]');
    }),
    {
      provide: DEFAULT_APP,
      useFactory: () => getApp('[DEFAULT]'),
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideAuth(() => {
      const app = inject(DEFAULT_APP);
      return getAuth(app);
    }),
    MessageService,
    ConfirmationService,
    RequestCounterService,
  ],
};
